import React from 'react';
import { Formik, Form } from 'formik';
import Button from '../../components/button';
import FormSelect from '../../components/dropdown/formSelect';
import InputText from '../../components/input-text/inputText';

const SellSharesInput = ({
  itemId,
  value,
  max,
  error,
  onCommit,
}: {
  itemId: string;
  value: number;
  max: number;
  error?: string;
  onCommit: (itemId: string, field: string, value: number) => void;
}) => {
  return (
    <Formik initialValues={{ sellShares: value || '' }} enableReinitialize onSubmit={() => {}}>
      {({ setFieldValue }) => (
        <Form>
          <div style={{ padding: '16px 0px 0px 0px ' }}>
            <InputText
              name='sellShares'
              type='number'
              placeholder=''
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('sellShares', e.target.value);
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                onCommit(itemId, 'sellShares', parseFloat(e.target.value) || 0);
              }}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              width='80px'
              errorValue={!!error}
              touchedValue={!!error}
              className='sell-shares-input'
            />
          </div>
          {!!error && (
            <div className='field-error-message' style={{ fontSize: '11px', marginTop: '2px' }}>
              {error}
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

interface BrokerSellItemsHeaderProps {
  actions: Record<string, any>[];
  bookingTypeOptions: any[];
  isBookingTypesLoading: boolean;
  settlementTypeOptions: any[];
  isSettlementTypesLoading: boolean;
  onAction: (row: any) => void;
  onCalcExercise: (row: any) => void;
  onViewExercise: (row: any) => void;
  onUpdateRow: (itemId: string, field: string, value: string | number) => void;
  submittingItemId: string | null;
  calculatingItemId: string | null;
  canSubmitBA: boolean;
  canFillDT: boolean;
  bookingTypeLabel?: string;
}

interface CellProps {
  row: any;
  cell: any;
}

const BrokerSellItemsHeader = ({
  actions,
  bookingTypeOptions,
  isBookingTypesLoading,
  settlementTypeOptions,
  isSettlementTypesLoading,
  onAction,
  onCalcExercise,
  onViewExercise,
  onUpdateRow,
  submittingItemId,
  calculatingItemId,
  canSubmitBA,
  canFillDT,
  bookingTypeLabel = 'Broker Assist',
}: BrokerSellItemsHeaderProps) => {
  const renderSellSharesCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width, textAlign: 'center' }}>
        <SellSharesInput
          itemId={original.itemId}
          value={original.sellShares || 0}
          max={original.maxShares}
          error={original.rowError}
          onCommit={onUpdateRow}
        />
      </td>
    );
  };

  const renderSettlementTypeCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    const isOption = original?.itemType === 'OptionExercise';
    if (!isOption) {
      return (
        <td
          style={{ minWidth: cell.column.minWidth, width: cell.column.width, textAlign: 'center' }}>
          <span style={{ color: '#999', fontSize: '11px' }}>-</span>
        </td>
      );
    }
    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width }}>
        <div style={{ padding: '16px 0px 0px 0px ' }}>
          <FormSelect
            type='text'
            name={`settlementType_${original.itemId}`}
            id={`settlementType_${original.itemId}`}
            placeholder='Select...'
            options={settlementTypeOptions ?? []}
            isLoading={isSettlementTypesLoading}
            values={original.settlementTypeId || ''}
            arrValue={original.settlementTypeId || ''}
            isFormik={false}
            onChange={(value: string) => {
              onUpdateRow(original.itemId, 'settlementTypeId', value);
            }}
            width='200px'
          />
        </div>
      </td>
    );
  };

  const renderBookingTypeCell = ({ row, cell }: CellProps) => {
    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width, textAlign: 'center' }}>
        <span style={{ fontSize: '14px' }}>{bookingTypeLabel}</span>
      </td>
    );
  };

  const renderCalcResultCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    const calcResult = original?.calcExResult;
    const isLoading = original?.isCalcLoading;

    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width }}>
        {isLoading ? (
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>Calculating...</span>
        ) : calcResult ? (
          <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
            <div>
              <strong>Net Cash:</strong> ${calcResult.netCash?.toLocaleString() ?? '-'}
            </div>
            <div>
              <strong>Net Shares:</strong> {calcResult.netShare?.toLocaleString() ?? '-'}
            </div>
            <div>
              <strong>Funding:</strong> ${calcResult.fundingRequired?.toLocaleString() ?? '-'}
            </div>
          </div>
        ) : (
          <span style={{ color: '#9ca3af', fontSize: '11px' }}>-</span>
        )}
      </td>
    );
  };

  const renderActionCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    const isOption = original?.itemType === 'OptionExercise';
    const isOptionIntent = original?.recordTypeName === 'Option Intent';
    const isBrokerAssist = (original?.bookingType || 'BrokerAssist') === 'BrokerAssist';
    const isThisRowSubmitting = submittingItemId === original?.itemId;
    const isThisRowCalculating = calculatingItemId === original?.itemId;
    const isAnySubmitting = submittingItemId !== null;
    const isAnyCalculating = calculatingItemId !== null;
    const showCalcButton = isOption && !isOptionIntent;
    const isRowCalcLoading = !!original?.isCalcLoading;

    return (
      <td
        style={{
          minWidth: cell.column.minWidth,
          width: cell.column.width,
          textAlign: 'center',
        }}>
        <div
          style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
          {isBrokerAssist ? (
            <span title='Submit'>
              <Button
                variant='rounded-icon-secondary'
                icon='save'
                type='button'
                title=''
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAction(original);
                }}
                disabled={isAnySubmitting || !canSubmitBA || isRowCalcLoading}
                loading={isThisRowSubmitting || isRowCalcLoading}
              />
            </span>
          ) : (
            <span title='Submit'>
              <Button
                variant='rounded-icon-secondary'
                icon='save'
                type='button'
                title=''
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAction(original);
                }}
                disabled={isAnySubmitting || !canFillDT || isRowCalcLoading}
                loading={isThisRowSubmitting || isRowCalcLoading}
              />
            </span>
          )}
          {showCalcButton && (
            <span title='Calculate Exercise'>
              <Button
                variant='rounded-icon-secondary'
                icon='calculate'
                type='button'
                title=''
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCalcExercise(original);
                }}
                disabled={isAnyCalculating}
                loading={isThisRowCalculating}
              />
            </span>
          )}
          {isOptionIntent && (
            <span title='View'>
              <Button
                variant='rounded-icon-secondary'
                icon='visibility'
                type='button'
                title=''
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewExercise(original);
                }}
                disabled={!original?.calcExResult || isRowCalcLoading}
                loading={isRowCalcLoading}
              />
            </span>
          )}
        </div>
      </td>
    );
  };

  const columnsWithOverrides = (actions || []).map((col: any) => {
    if (col.accessor === 'sellShares') {
      return { ...col, renderCell: true, Cell: renderSellSharesCell };
    }
    if (col.accessor === 'settlementTypeId') {
      return { ...col, renderCell: true, Cell: renderSettlementTypeCell };
    }
    if (col.accessor === 'bookingType') {
      return { ...col, width: 120, minWidth: 120, renderCell: true, Cell: renderBookingTypeCell };
    }
    return col;
  });

  return [
    ...columnsWithOverrides,
    {
      Header: 'Action',
      accessor: 'sellAction',
      width: 100,
      sortable: false,
      renderCell: true,
      center: true,
      Cell: renderActionCell,
    },
  ];
};

export default BrokerSellItemsHeader;
